import { Controller } from "@hotwired/stimulus"

const VIEWPORT_MARGIN = 8

// Native <details> stays open on outside clicks and ignores Escape, and an absolutely positioned
// panel gets clipped by the admin table/card `overflow` wrappers. So the panel is `position: fixed`
// and anchored to the summary here.
export default class DisclosureController extends Controller<HTMLDetailsElement> {
  static targets = ["panel"]

  declare readonly panelTarget: HTMLElement
  declare readonly hasPanelTarget: boolean

  private readonly onDocumentPointerDown = (event: Event): void => {
    if (!this.element.open) return
    const target = event.target
    if (target instanceof Node && this.element.contains(target)) return
    this.close()
  }

  private readonly onDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || !this.element.open) return
    this.close()
    this.summary?.focus()
  }

  private readonly onReflow = (): void => {
    if (this.element.open) this.position()
  }

  connect(): void {
    document.addEventListener("pointerdown", this.onDocumentPointerDown, true)
    document.addEventListener("keydown", this.onDocumentKeydown)
    window.addEventListener("scroll", this.onReflow, true)
    window.addEventListener("resize", this.onReflow)
  }

  disconnect(): void {
    document.removeEventListener("pointerdown", this.onDocumentPointerDown, true)
    document.removeEventListener("keydown", this.onDocumentKeydown)
    window.removeEventListener("scroll", this.onReflow, true)
    window.removeEventListener("resize", this.onReflow)
  }

  toggle(): void {
    if (this.element.open) this.position()
  }

  private close(): void {
    this.element.open = false
  }

  private position(): void {
    if (!this.hasPanelTarget) return

    const summary = this.summary
    if (!summary) return

    const anchor = summary.getBoundingClientRect()
    const panel = this.panelTarget
    const { offsetWidth: width, offsetHeight: height } = panel

    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN
    const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.right - width, maxLeft))

    const below = anchor.bottom + VIEWPORT_MARGIN
    const fitsBelow = below + height <= window.innerHeight - VIEWPORT_MARGIN
    const top = fitsBelow ? below : Math.max(VIEWPORT_MARGIN, anchor.top - height - VIEWPORT_MARGIN)

    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
  }

  private get summary(): HTMLElement | null {
    return this.element.querySelector("summary")
  }
}
